import React, { useRef, useState } from 'react';
import { useDismissOnOutsideClick } from '../../hooks/useDismissOnOutsideClick';
import './TaggingForm.scss';
import {
  useTagFormForCurrentUser,
  useTagFormElementData,
  useSaveTagFormElementData,
  useDeleteTagFormElementData,
  type TagFormElement,
} from '../../hooks/useTagForm/useTagForm';
import { useTagSuggestions } from '../../hooks/useTagForm/useTagForm';
import { useAddResourceTag, useRemoveResourceTag } from '../../hooks/useResource';
import { ToggleSwitch } from '../ToggleSwitch';
import { useResourceViewerStore } from '../../stores/resourceViewerStore';
import type { Resource } from '../../types/resource';

interface Props {
  resource: Resource;
  projectId: string;
  collectionId: string;
  resourceId: string;
  tagRelated: boolean;
  onTagRelatedChange: (v: boolean) => void;
}

// ── Text element (multi-value combo) ────────────────────────────────────────

interface TextElementProps {
  element: TagFormElement;
  projectId: string;
  collectionId: string;
  resourceId: string;
}

function TextElement({ element, projectId, collectionId, resourceId }: TextElementProps) {
  const [inputValue, setInputValue] = useState('');
  const { data: items = [] } = useTagFormElementData(projectId, collectionId, resourceId, element.id);
  const save = useSaveTagFormElementData(projectId, collectionId, resourceId);
  const remove = useDeleteTagFormElementData(projectId, collectionId, resourceId);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      save.mutate({ tagFormElementId: element.id, value: inputValue.trim() });
      setInputValue('');
    }
  }

  return (
    <div className="admin-tag-form-element" style={{ display: 'table', width: '100%', marginBottom: 8, paddingLeft: 10 }} data-testid="viewer-form-element-item">
      <div style={{ display: 'table-cell', whiteSpace: 'nowrap', verticalAlign: 'middle', paddingRight: 8, fontSize: 13, color: '#555' }}>
        {element.name}:
      </div>
      <div style={{ display: 'table-cell', width: '100%' }}>
        {/* chip input: pills + text input rendered inside a box */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'center',
            border: '1px solid #ccc',
            borderRadius: 4,
            padding: '3px 6px',
            minHeight: 32,
            background: '#fff',
            cursor: 'text',
          }}
        >
          {items.map((item) => (
            <span key={item.id} className="tag-form-chip" data-testid="viewer-form-value-chip">
              {item.value}
              <i
                className="fas fa-times"
                onClick={() => remove.mutate({ id: item.id, elementId: element.id })}
                data-testid="viewer-form-value-remove-btn"
              />
            </span>
          ))}
          <input
            type="text"
            style={{ border: 'none', outline: 'none', flex: 1, minWidth: 80, fontSize: 13, padding: 0 }}
            placeholder={items.length === 0 ? 'Type and press Enter' : ''}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={save.isPending}
            data-testid="viewer-form-text-input"
          />
        </div>
      </div>
    </div>
  );
}

// ── Checkbox / radio element ─────────────────────────────────────────────────

interface ToggleElementProps {
  element: TagFormElement;
  resource: Resource;
  tagRelated: boolean;
  addTag: ReturnType<typeof useAddResourceTag>;
  removeTag: ReturnType<typeof useRemoveResourceTag>;
}

function ToggleElement({ element, resource, tagRelated, addTag, removeTag }: ToggleElementProps) {
  const mainTag = element.tag;
  const inverseTag = element.inverseTag;

  if (!mainTag) return null;

  const mainApplied = resource.tags.some((t) => t.id === mainTag.id);
  const inverseApplied = inverseTag ? resource.tags.some((t) => t.id === inverseTag.id) : false;

  function toggle(tagId: string, tagName: string, isApplied: boolean, otherTagId?: string) {
    if (isApplied) {
      removeTag.mutate({ tagId, withRelated: tagRelated });
    } else {
      // If the other radio button is active, remove it first
      if (otherTagId) {
        const otherApplied = resource.tags.some((t) => t.id === otherTagId);
        if (otherApplied) removeTag.mutate({ tagId: otherTagId, withRelated: tagRelated });
      }
      addTag.mutate({ name: tagName, tagRelated });
    }
  }

  // Children of whichever side is applied pop out as extra checkboxes (Angular admin_tag_input.html).
  const activeChildren = (mainApplied ? element.children : inverseApplied ? element.inverseChildren : null) ?? [];
  const childOptions = activeChildren.length > 0 && (
    <div className="col-sm-offset-1 pull-left admin-tag-form-element-children">
      {[...activeChildren].sort((a, b) => a.name.localeCompare(b.name)).map((child) => {
        const childApplied = resource.tags.some((t) => t.id === child.id);
        return (
          <button
            key={child.id}
            type="button"
            className={`btn btn-sm btn-default${childApplied ? ' active' : ''}`}
            onClick={() => toggle(child.id, child.name, childApplied)}
            data-testid="viewer-form-child-btn"
          >
            <i className={`far ${childApplied ? 'fa-check-square' : 'fa-square'}`} />
            {' '}{child.name}
          </button>
        );
      })}
    </div>
  );

  if (element.type === 'radio' && inverseTag) {
    return (
      <div className="admin-tag-form-element" style={{ marginBottom: 4 }} data-testid="viewer-form-element-item">
        <div className="btn-group pull-left" style={{ paddingLeft: 10 }}>
          <button
            type="button"
            className={`btn btn-sm btn-default${mainApplied ? ' active' : ''}`}
            onClick={() => toggle(mainTag.id, mainTag.name, mainApplied, inverseTag.id)}
            data-testid="viewer-form-radio-btn"
          >
            <i className={`far ${mainApplied ? 'fa-dot-circle' : 'fa-circle'}`} />
            {' '}{mainTag.name}
          </button>
          <button
            type="button"
            className={`btn btn-sm btn-default${inverseApplied ? ' active' : ''}`}
            onClick={() => toggle(inverseTag.id, inverseTag.name, inverseApplied, mainTag.id)}
            data-testid="viewer-form-radio-btn"
          >
            <i className={`far ${inverseApplied ? 'fa-dot-circle' : 'fa-circle'}`} />
            {' '}{inverseTag.name}
          </button>
        </div>
        <div style={{ clear: 'both' }} />
        {childOptions}
        <div style={{ clear: 'both' }} />
      </div>
    );
  }

  // checkbox
  return (
    <div className="admin-tag-form-element" style={{ marginBottom: 4 }} data-testid="viewer-form-element-item">
      <div className="btn-group pull-left" style={{ paddingLeft: 10 }}>
        <button
          type="button"
          className={`btn btn-sm btn-default${mainApplied ? ' active' : ''}`}
          onClick={() => toggle(mainTag.id, mainTag.name, mainApplied)}
          data-testid="viewer-form-checkbox-btn"
        >
          <i className={`far ${mainApplied ? 'fa-check-square' : 'fa-square'}`} />
          {' '}{mainTag.name}
        </button>
      </div>
      <div style={{ clear: 'both' }} />
      {childOptions}
      <div style={{ clear: 'both' }} />
    </div>
  );
}

// ── Simple free-text add-tag form ────────────────────────────────────────────

interface SimpleTagFormProps {
  resource: Resource;
  projectId: string;
  tagRelated: boolean;
  addTag: ReturnType<typeof useAddResourceTag>;
  removeTag: ReturnType<typeof useRemoveResourceTag>;
}

function SimpleTagForm({ resource, projectId, tagRelated, addTag, removeTag }: SimpleTagFormProps) {
  const [tagInput, setTagInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const typeaheadRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(typeaheadRef, showSuggestions, () => setShowSuggestions(false));
  const { data: suggestions = [] } = useTagSuggestions(projectId, tagInput, false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!tagInput.trim()) return;
    addTag.mutate({ name: tagInput.trim(), tagRelated }, { onSuccess: () => setTagInput('') });
  }

  return (
    <>
      <form className="form-inline" onSubmit={handleSubmit} style={{ marginBottom: 8, position: 'relative' }}>
        <div className="form-group" style={{ position: 'relative' }} ref={typeaheadRef}>
          <input
            type="text"
            className="form-control"
            value={tagInput}
            onChange={(e) => { setTagInput(e.target.value); setShowSuggestions(true); }}
            onFocus={() => tagInput.length > 0 && setShowSuggestions(true)}
            disabled={addTag.isPending}
            autoComplete="off"
            data-testid="viewer-tag-input"
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul className="dropdown-menu" style={{ display: 'block', width: '100%', maxHeight: 200, overflowY: 'auto' }} data-testid="viewer-tag-suggestion-list">
              {suggestions.map((s) => (
                <li key={s?.id}>
                  <a onMouseDown={() => { setTagInput(s?.name ?? ''); setShowSuggestions(false); }} data-testid="viewer-tag-suggestion-item">
                    {s?.name}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
        {' '}
        <button type="submit" className="btn btn-primary" disabled={addTag.isPending || !tagInput.trim()} data-testid="viewer-tag-add-btn">
          {addTag.isPending && <i className="fas fa-spinner fa-spin" />}
          {' '}Tag <i className="fas fa-plus-circle" />
        </button>
      </form>

      {resource.tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {[...resource.tags]
            .sort((a, b) => a.categoryId.localeCompare(b.categoryId) || a.name.localeCompare(b.name))
            .map((tag) => (
              <div key={tag.id} className="tag" data-testid="viewer-tag-chip">
                <div className="btn-group">
                  <button className="btn btn-sm btn-default">
                    <i className="fas fa-circle" style={{ color: tag.color ?? undefined }} />
                    {' '}{tag.name}
                  </button>
                  {tag.isNameEditable && (
                    <button
                      className="btn btn-default btn-sm"
                      title="Remove tag"
                      data-testid="viewer-tag-remove-btn"
                      onClick={() => removeTag.mutate({ tagId: tag.id, withRelated: tagRelated })}
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </>
  );
}

// ── Main TaggingForm ─────────────────────────────────────────────────────────

export default function TaggingForm({
  resource,
  projectId,
  collectionId,
  resourceId,
  tagRelated,
  onTagRelatedChange,
}: Props) {
  const advancedOpen = useResourceViewerStore((s) => s.advancedTaggingOpen);
  const setAdvancedOpen = useResourceViewerStore((s) => s.setAdvancedTaggingOpen);
  const { data: tagForm } = useTagFormForCurrentUser(projectId);
  const addTag = useAddResourceTag(projectId, collectionId, resourceId);
  const removeTag = useRemoveResourceTag(projectId, collectionId, resourceId);

  const hasElements = (tagForm?.elements?.length ?? 0) > 0;

  return (
    <div>
      <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Tagging
        <small style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal' }}>
          Apply to Related
          <ToggleSwitch checked={tagRelated} onChange={onTagRelatedChange} size="small" testId="viewer-apply-related-toggle" />
        </small>
      </h4>

      {hasElements && (
        <>
          {tagForm!.elements.map((el) =>
            el.type === 'text' ? (
              <TextElement
                key={el.id}
                element={el}
                projectId={projectId}
                collectionId={collectionId}
                resourceId={resourceId}
              />
            ) : (
              <ToggleElement
                key={el.id}
                element={el}
                resource={resource}
                tagRelated={tagRelated}
                addTag={addTag}
                removeTag={removeTag}
              />
            )
          )}
          <br />
          <a
            style={{ cursor: 'pointer', fontSize: 12 }}
            onClick={() => setAdvancedOpen(!advancedOpen)}
            data-testid="viewer-advanced-tagging-link"
          >
            {advancedOpen ? 'Hide Advanced Tagging' : 'Advanced Tagging'}
          </a>
        </>
      )}

      {(!hasElements || advancedOpen) && (
        <div style={{ marginTop: hasElements ? 8 : 0 }}>
          <SimpleTagForm
            resource={resource}
            projectId={projectId}
            tagRelated={tagRelated}
            addTag={addTag}
            removeTag={removeTag}
          />
        </div>
      )}
    </div>
  );
}
